import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, LockKeyhole, ShieldCheck, X } from 'lucide-react';
interface PortfolioGateProps {
  isOpen: boolean;
  onClose: () => void;
  onUnlocked: () => void;
}
// Demo-only front-end password. This is not secure authentication and must not
// be used to protect genuinely confidential work.
export const PORTFOLIO_ACCESS_PASSWORD = 'byteweb-client';
export const PORTFOLIO_ACCESS_SESSION_KEY = 'byteweb-portfolio-access';
export function PortfolioGate({
  isOpen,
  onClose,
  onUnlocked
}: PortfolioGateProps) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (isOpen) {
      window.setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);
  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (password === PORTFOLIO_ACCESS_PASSWORD) {
      setPassword('');
      setError('');
      onUnlocked();
      return;
    }
    setError('That access password is not recognised. Please try again.');
  };
  const handleClose = () => {
    setPassword('');
    setError('');
    onClose();
  };
  return <AnimatePresence data-id="element-153">
      {isOpen && <motion.div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} role="dialog" aria-modal="true" aria-labelledby="portfolio-gate-title" data-id="element-154">
          <button type="button" className="absolute inset-0 bg-true-black/55 backdrop-blur-sm" aria-label="Close private work access" onClick={handleClose} data-id="element-155" />
          <motion.div initial={{
        opacity: 0,
        y: 18,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 12,
        scale: 0.98
      }} transition={{
        duration: 0.24,
        ease: 'easeOut'
      }} className="relative w-full max-w-lg overflow-hidden rounded-2xl border border-white/10 bg-dark-lighter p-7 shadow-2xl sm:p-10" data-id="element-156">
            <button type="button" onClick={handleClose} className="absolute right-5 top-5 inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-white/60 transition-colors hover:border-gold hover:text-gold focus:outline-none focus:ring-2 focus:ring-gold" aria-label="Close private work access" data-id="element-157">
              <X size={19} data-id="element-158" />
            </button>

            <div className="mb-8 pr-10" data-id="element-159">
              <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl border border-gold/30 bg-gold/10 text-gold" data-id="element-160">
                <LockKeyhole size={25} data-id="element-161" />
              </div>
              <p className="mb-2 font-inter text-xs font-bold uppercase tracking-[0.2em] text-gold" data-id="element-162">
                Client access
              </p>
              <h2 id="portfolio-gate-title" className="font-syne text-3xl font-bold text-white sm:text-4xl" data-id="element-163">
                Professional profile, by invitation.
              </h2>
              <p className="mt-4 max-w-md font-inter leading-relaxed text-white/65" data-id="element-164">
                Enter the access password shared with you to view the
                professional profile and detailed project archive.
              </p>
            </div>

            <form onSubmit={handleSubmit} noValidate data-id="element-165">
              <label htmlFor="portfolio-password" className="mb-2 block font-inter text-sm font-semibold text-white" data-id="element-166">
                Access password
              </label>
              <input ref={inputRef} id="portfolio-password" type="password" value={password} onChange={event => {
            setPassword(event.target.value);
            if (error) setError('');
          }} className="w-full rounded-xl border border-white/15 bg-dark px-4 py-3.5 font-inter text-white outline-none transition-colors placeholder:text-white/35 focus:border-gold focus:ring-2 focus:ring-gold/20" placeholder="Enter password" autoComplete="current-password" autoCapitalize="none" spellCheck={false} aria-invalid={Boolean(error)} aria-describedby={error ? 'portfolio-password-error' : undefined} data-id="element-167" />
              <div className="min-h-6 pt-2" aria-live="polite" data-id="element-168">
                {error && <p id="portfolio-password-error" className="font-inter text-sm text-red-400" data-id="element-169">
                    {error}
                  </p>}
              </div>

              <button type="submit" className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-true-black bg-gold px-5 py-3.5 font-inter font-bold text-true-white transition-transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-gold focus:ring-offset-2 focus:ring-offset-dark-lighter" data-id="element-170">
                View professional profile
                <ArrowRight size={18} data-id="element-171" />
              </button>
            </form>

            <div className="mt-7 flex items-start gap-3 border-t border-white/10 pt-5" data-id="element-172">
              <ShieldCheck size={18} className="mt-0.5 shrink-0 text-gold" data-id="element-173" />
              <p className="font-inter text-xs leading-relaxed text-white/45" data-id="element-174">
                Access remains available for this browser session only. Need an
                invitation? Book a consultation and we’ll share the right next
                step.
              </p>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}